import { EDU_STATUS } from '../store/eduSlice';
import type { RootState } from '../store/store';
import { isFamilyIneligible, productFamily } from '../utils/utils';

export interface NoticeRule {
  id: string;
  blocking: boolean;
  variant: 'error' | 'success';
  matches: (state: RootState) => boolean;
  title: string;
  body?: string;
  /** Renders `body` through `<Trans>`, filling its `<link>` tag with this href. */
  bodyLink?: () => string;
}

const isLicenseComplianceCode = (state: RootState) =>
  state.cart.productsPreview?.promoItems?.promoCode?.slice(-2) === 'LC';

const FREE_CREDITS_CODE = 'BOOST3000';
const RELEASE_CREDITS_CODE = 'RELEASE26';

const isPromoApplied = (state: RootState, code: string) => {
  const preview = state.cart.productsPreview;
  return (
    preview?.promoItems?.promoCode === code && preview.promoStatus === 'Applied'
  );
};

const CELLO_CHARGE_NAME = 'Cello Referral Discount';
const CELLO_MAX_SEATS = 3;

const isManagedUser = (state: RootState) =>
  state.billing.info?.code === 'MANAGED_USER';

const hasLicenseViolation = (state: RootState) =>
  Boolean(state.app.eligibility?.reason?.includes('license_violation'));

export const noticeRules: NoticeRule[] = [
  {
    id: 'account-cancelled',
    blocking: true,
    variant: 'error',
    matches: (state) => state.billing.info?.code === 'CANCELED',
    title: 'Your account is cancelled.',
  },
  {
    id: 'managed-user-license-violation',
    blocking: true,
    variant: 'error',
    matches: (state) => isManagedUser(state) && hasLicenseViolation(state),
    title:
      'This order cannot be processed at this time. Please contact the Chaos License Compliance Team for assistance.',
  },
  {
    id: 'managed-user',
    blocking: true,
    variant: 'error',
    matches: (state) => isManagedUser(state) && !hasLicenseViolation(state),
    title: 'Managed users are not allowed to buy products.',
  },
  {
    id: 'license-compliance-applied',
    blocking: false,
    variant: 'success',
    matches: (state) =>
      isLicenseComplianceCode(state) &&
      state.cart.productsPreview?.promoStatus === 'Applied',
    title: 'Amnesty discount applied.',
  },
  {
    id: 'license-compliance-invalid',
    blocking: false,
    variant: 'error',
    matches: (state) =>
      isLicenseComplianceCode(state) &&
      state.cart.productsPreview?.promoStatus === 'Invalid',
    title: 'Code not valid or already used.',
    body: 'Contact us at licensecompliance@chaos.com to resolve your case.',
  },
  {
    id: 'free-credits-applied',
    blocking: false,
    variant: 'success',
    matches: (state) => isPromoApplied(state, FREE_CREDITS_CODE),
    title: 'Promo Applied.',
    body: '3,000 free Chaos Credits are included with each eligible product on this order! Your total credits will be provisioned to your account upon completion of checkout.',
  },
  {
    id: 'release-credits-applied',
    blocking: false,
    variant: 'success',
    matches: (state) => isPromoApplied(state, RELEASE_CREDITS_CODE),
    title: 'Promo Applied.',
    body: '500 free Chaos Credits are included with this order! Credits will be provisioned once upon checkout completion.',
  },
  {
    id: 'edu-status-rejected',
    blocking: true,
    variant: 'error',
    matches: (state) =>
      Boolean(state.cart.catalog?.hasEduProduct) &&
      state.eduStatus.status === EDU_STATUS.REJECTED,
    title: 'Your educational status has not been confirmed.',
    body: 'Please email us at education@chaos.com if you would like to apply again and unlock the educational discount.',
  },
  {
    id: 'edu-active-student-subscription',
    blocking: true,
    variant: 'error',
    matches: (state) => {
      const activeCodes = state.app.eligibility?.activeStudentProductCodes;
      if (!activeCodes?.length) return false;
      const items = state.cart.catalog?.items ?? [];
      return items.some((item) => activeCodes.includes(item.productCode));
    },
    title: 'You already have an active EDU subscription for this product.',
  },
  {
    id: 'trial-ineligible',
    blocking: true,
    variant: 'error',
    matches: (state) => {
      const families = state.app.eligibility?.ineligibleTrialFamilies;
      const productCode = state.trial.trialCatalog?.productCode;
      if (!families?.length || !productCode) return false;
      return isFamilyIneligible(productFamily(productCode), families);
    },
    title: 'You already have an active or expired license.',
    body: 'You already have an active or expired license for this product. Please <link>manage or upgrade</link> your subscription from your account.',
    bodyLink: () => window.MYCHAOS,
  },
  {
    id: 'cello-referral-limit',
    blocking: true,
    variant: 'error',
    matches: (state) => {
      const preview = state.cart.productsPreview;
      if (preview?.promoItems?.list?.[0]?.chargeName !== CELLO_CHARGE_NAME) {
        return false;
      }
      const seats =
        preview.invoiceItems?.reduce((sum, item) => sum + item.quantity, 0) ??
        0;
      return seats > CELLO_MAX_SEATS;
    },
    title: 'Referral limit exceeded.',
    body: 'You can purchase up to 3 licenses via the referral program. Please reduce the amount of licenses in your cart and start designing!',
  },
];
